import * as THREE from "three";
import { PROSTATE_QUADRANT } from "../../consts";
import StructureModel from "./StructureModel";
import { volumeNames } from "../../../../../constants";

export default class CRMModel extends StructureModel {
    constructor(prostateStructure) {
        super();

        // use the same viewers as the prostate
        this.mriViewer = prostateStructure.mriViewer;

        // NB: Do not set the meshViewer, because we don't want the mesh to be
        //     created. But we'll keep this line here for the future, in case we
        //     need it for debugging or maybe someday do want to show it.
        //this.meshViewer = prostateStructure.meshViewer;

        // create the mesh objects for the MRI view
        this.initializeViewFromGeometry(prostateStructure);

        // don't show any of the CRM by default
        this.quadrant = PROSTATE_QUADRANT.NONE;
    }

    initializeViewFromGeometry(prostateStructure) {
        let geometry = this._cloneAndExplodeGeometry(
            prostateStructure.geometry,
            3.0
        );
        let center = new THREE.Vector3();

        // find the center of the original, prostate geometry
        center.copy(prostateStructure.center);
        //center.applyMatrix4(LPS2RAS);

        // TODO: The center of the geometry doesn't work perfectly. Instead,
        //       it would be better to get a set of center coordinates along
        //       the Z-axis and then - when rendering - provide the correct
        //       one for the closest Z coordinate.

        super.initializeViewFromGeometry(geometry, {
            name: volumeNames.MR_CRM,
            geometry: geometry,
            color: "#ffff00",
        });

        // if this.meshViewer is null, then the mesh view won't be created
        if (this._selfMeshView) {
            this._selfMeshView.visible = false;
        }

        // add the shader uniforms
        this._selfMRIView["contourUniforms"] = {
            meshCenter: center,
            dashed: true,
            dashedGap: 3,
            quadrant: PROSTATE_QUADRANT.ALL,
            showAxes: true,
        };
    }

    get quadrant() {
        return this._selfMRIView.contourUniforms.quadrant;
    }

    set quadrant(prostateQuadrant) {
        this._selfMRIView.contourUniforms.quadrant = prostateQuadrant;

        // This section is for quadrant specific CRM prostate contours (not in use)
        // this.visibility = prostateQuadrant !== PROSTATE_QUADRANT.NONE;
        this.visibility = false;

        // The mriViewer has the MRIModel attached during loadMriStep4
        if (this.mriViewer) {
            const mriModel = this.mriViewer.mriModel;
            if (mriModel && mriModel.showQuadrantRect) {
                mriModel.showQuadrantRect(prostateQuadrant);
            }
        }
    }

    get showAxes() {
        return this._selfMRIView.contourUniforms.showAxes;
    }

    set showAxes(show) {
        this._selfMRIView.contourUniforms.showAxes = show;
    }

    _cloneAndExplodeGeometry(prostateGeometry, radius) {
        let geometry = prostateGeometry.clone();

        /* There are two things to be aware of when trying to "explode" or
         * scale the geometry mesh for the CRM:
         *
         *  1. Scaling along the Z axis (head <-> feet) would be incorrect.
         *     This is because it would mis-align the geometry with the MRI
         *     slice.
         *
         *  2. The vertices for the prostate have been already transformed
         *     in the VTK to be in world space, aligned with the rest of
         *     the imaging data.
         *
         *  3. The prostate mesh is concave, meaning that when scaled, not
         *     all the vertices will push away from their original vertex
         *     in the desired direction (along their normal as opposed to
         *     away from the origin).
         *
         * For these reasons, it's not possible to "scale" the geometry about
         * the local origin. Instead, what needs to be done is that every
         * vertex needs to be uniformly "pushed" away, along the X/Y plane,
         * in the direction of its normal.
         */

        geometry.computeVertexNormals();

        // move each of the vertices in the direction of the normal
        const vertices = geometry.getAttribute("position");
        const normals = geometry.getAttribute("normal");

        for (let i = 0; i < vertices.count; i++) {
            let x = vertices.getX(i);
            let y = vertices.getY(i);
            let z = vertices.getZ(i);

            // get the normal, but only along the x/y plane
            let n = new THREE.Vector2(normals.getX(i), normals.getY(i));

            // don't offset vertices at the poles
            if (n.lengthSq() > 0.05) {
                n.normalize();

                // update the vertices
                vertices.setXYZ(i, x + n.x * radius, y + n.y * radius, z);
            }
        }

        // probably not needed, but what the heck?
        geometry.needsUpdate = true;

        // calculate bounding boxes and new vertex normals
        geometry.computeVertexNormals();
        geometry.computeBoundingBox();
        geometry.computeBoundingSphere();

        return geometry;
    }
}
